import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Paper, Typography, TextField, Button, Autocomplete, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, Chip, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import {
  Print as PrintIcon,
  Search as SearchIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MonetizationOn as MoneyIcon,
  Replay as ReplayIcon,
  FileDownload as ExcelIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import ExcelJS from 'exceljs';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import apiClient from '../services/api';
import { EmployeeCustodySummaryResult } from '../types/custodyStatement.types';

// ============================================================
// ترجمات
// ============================================================
const translations = {
  en: {
    title: 'Employee Custody Summary',
    subtitle: 'View all custodies for a specific employee',
    selectEmployee: 'Select Employee',
    filterStatus: 'Status',
    all: 'All',
    search: 'Search',
    print: 'Print',
    exportExcel: 'Excel',
    reset: 'Reset',
    noData: 'No custodies found',
    selectEmployeeFirst: 'Please select an employee to view their custody summary',
    exportSuccess: 'Excel exported successfully',
    kwd: 'KWD',
    totalCustodies: 'Total Custodies',
    openCustodies: 'Open Custodies',
    totalInitial: 'Total Initial Amount',
    totalBalance: 'Total Balance',
    totalSpent: 'Total Spent',
    totalReturned: 'Total Returned',
    rowNum: '#',
    custodyNumber: 'Custody #',
    date: 'Date',
    branch: 'Branch',
    account: 'Account',
    initialAmount: 'Initial Amount',
    spent: 'Spent',
    returned: 'Returned',
    balance: 'Balance',
    status: 'Status',
    totals: 'Totals',
    Open: 'Open',
    Settled: 'Settled',
    Closed: 'Closed',
    open: 'Open',
    settled: 'Settled',
    closed: 'Closed',
    custodyHolder: 'Custody Holder',
    accountant: 'Accountant',
    manager: 'Manager',
  },
  ar: {
    title: 'ملخص عُهد الموظف',
    subtitle: 'عرض جميع العُهد لموظف محدد',
    selectEmployee: 'اختر الموظف',
    filterStatus: 'الحالة',
    all: 'الكل',
    search: 'بحث',
    print: 'طباعة',
    exportExcel: 'إكسل',
    reset: 'إعادة تعيين',
    noData: 'لا توجد عُهد',
    selectEmployeeFirst: 'يرجى اختيار موظف لعرض ملخص عُهده',
    exportSuccess: 'تم التصدير لإكسل بنجاح',
    kwd: 'د.ك',
    totalCustodies: 'إجمالي العُهد',
    openCustodies: 'العُهد المفتوحة',
    totalInitial: 'إجمالي المبالغ الأولية',
    totalBalance: 'إجمالي الأرصدة',
    totalSpent: 'إجمالي المنصرف',
    totalReturned: 'إجمالي المسترد',
    rowNum: 'م',
    custodyNumber: 'رقم العهدة',
    date: 'التاريخ',
    branch: 'الفرع',
    account: 'الحساب',
    initialAmount: 'المبلغ الأولي',
    spent: 'المنصرف',
    returned: 'المسترد',
    balance: 'الرصيد',
    status: 'الحالة',
    totals: 'الإجماليات',
    Open: 'مفتوحة',
    Settled: 'مُسوّاة',
    Closed: 'مغلقة',
    open: 'مفتوحة',
    settled: 'مُسوّاة',
    closed: 'مغلقة',
    custodyHolder: 'حامل العهدة',
    accountant: 'المحاسب',
    manager: 'المدير',
  },
};

const EmployeeCustodySummaryPage: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const t = translations[isRTL ? 'ar' : 'en'];

  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EmployeeCustodySummaryResult | null>(null);
  const [companyInfo, setCompanyInfo] = useState<any>(null);

  useEffect(() => {
    loadEmployees();
    loadCompanyInfo();
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await apiClient.get('/Custodies/lookup');
      const custodies = Array.isArray(response.data) ? response.data : [];
      const uniqueMap = new Map<number, any>();
      custodies.forEach((c: any) => {
        if (c.employeeId && !uniqueMap.has(c.employeeId)) {
          uniqueMap.set(c.employeeId, {
            employeeId: c.employeeId,
            fullName: c.employeeName,
            fullNameAr: c.employeeNameAr || c.employeeName,
            employeeCode: '',
          });
        }
      });
      setEmployees(Array.from(uniqueMap.values()));
    } catch (error) { console.error('Failed to load employees:', error); }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await apiClient.get('/CompanySettings');
      setCompanyInfo(response.data);
    } catch (error) { console.error('Failed to load company info:', error); }
  };

  const handleSearch = async () => {
    if (!selectedEmployee) {
      toast.warning(t.selectEmployeeFirst, { position: 'top-center' });
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      const response = await apiClient.get(`/Custodies/employee/${selectedEmployee.employeeId}/custody-summary?${params.toString()}`);
      setResult(response.data);
    } catch (error: any) {
      console.error('Failed to load summary:', error);
      toast.error(isRTL ? 'خطأ في تحميل الملخص' : 'Failed to load summary', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedEmployee(null);
    setStatusFilter('');
    setFromDate('');
    setToDate('');
    setResult(null);
  };

  const getStatusColor = (status: string): 'info' | 'success' | 'warning' | 'default' => {
    switch (status) {
      case 'Open': return 'info';
      case 'Settled': return 'success';
      case 'Closed': return 'warning';
      default: return 'default';
    }
  };

  // ============================================================
  // طباعة
  // ============================================================
  const handlePrint = () => {
    if (!result) return;
    const pw = window.open('', '_blank');
    if (!pw) return;
    const e = result.employee;
    const tt = result.totals;
    const logoBase64 = companyInfo?.logoBase64 || '';
    const companyName = isRTL ? (companyInfo?.companyNameAr || companyInfo?.companyName || '') : (companyInfo?.companyName || '');
    const companyAddress = isRTL ? (companyInfo?.addressAr || companyInfo?.address || '') : (companyInfo?.address || '');
    const companyPhone = companyInfo?.phone || '';

    pw.document.write(`
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${t.title} - ${isRTL ? e.employeeNameAr : e.employeeName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; font-size: 12px; padding: 20px; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          .company-header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #1976d2; }
          .company-logo { max-height: 80px; max-width: 200px; margin-bottom: 8px; }
          .company-name { font-size: 20px; font-weight: bold; color: #1976d2; margin-bottom: 4px; }
          .company-details { font-size: 11px; color: #666; }
          .report-title { text-align: center; font-size: 16px; font-weight: bold; margin: 15px 0 5px; color: #333; background: #e3f2fd; padding: 8px; border-radius: 4px; }
          .report-sub { text-align: center; font-size: 13px; color: #555; margin-bottom: 15px; font-weight: bold; }
          .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 15px; }
          .stat-card { padding: 8px; text-align: center; border: 1px solid #ddd; border-radius: 4px; }
          .stat-label { font-size: 10px; color: #666; }
          .stat-value { font-size: 16px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; }
          th { background: #1976d2; color: white; padding: 6px 4px; text-align: center; font-weight: bold; }
          td { padding: 5px 4px; border-bottom: 1px solid #ddd; text-align: center; }
          tr:nth-child(even) { background: #fafafa; }
          .positive { color: #2e7d32; font-weight: bold; }
          .negative { color: #c62828; font-weight: bold; }
          .totals-row { background: #e3f2fd !important; font-weight: bold; }
          .signatures { display: grid; grid-template-columns: repeat(3,1fr); gap: 30px; margin-top: 40px; padding-top: 15px; }
          .sig-line { text-align: center; }
          .sig-line div { border-top: 1px solid #333; width: 70%; margin: 0 auto; padding-top: 8px; font-weight: bold; font-size: 12px; }
          .footer { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #999; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="company-header">
          ${logoBase64 ? `<img src="${logoBase64}" class="company-logo" /><br/>` : ''}
          <div class="company-name">${companyName}</div>
          <div class="company-details">
            ${companyAddress ? companyAddress + '<br/>' : ''}
            ${companyPhone ? (isRTL ? 'هاتف: ' : 'Tel: ') + companyPhone : ''}
          </div>
        </div>
        <div class="report-title">${t.title}</div>
        <div class="report-sub">${isRTL ? e.employeeNameAr : e.employeeName}${statusFilter ? ` (${(t as any)[statusFilter] || statusFilter})` : ''}</div>
        <div class="stats">
          <div class="stat-card"><div class="stat-label">${t.totalCustodies}</div><div class="stat-value" style="color:#1976d2">${e.totalCustodies}</div></div>
          <div class="stat-card"><div class="stat-label">${t.totalInitial}</div><div class="stat-value" style="color:#2e7d32">${tt.totalInitialAmount.toFixed(3)}</div></div>
          <div class="stat-card"><div class="stat-label">${t.totalSpent}</div><div class="stat-value" style="color:#c62828">${tt.totalSpent.toFixed(3)}</div></div>
          <div class="stat-card"><div class="stat-label">${t.totalBalance}</div><div class="stat-value" style="color:${tt.totalCurrentBalance < 0 ? '#c62828' : '#1976d2'}">${tt.totalCurrentBalance.toFixed(3)}</div></div>
        </div>
        <table>
          <thead><tr>
            <th>${t.rowNum}</th><th>${t.custodyNumber}</th><th>${t.date}</th><th>${t.branch}</th><th>${t.account}</th>
            <th>${t.initialAmount}</th><th>${t.spent}</th><th>${t.returned}</th><th>${t.balance}</th><th>${t.status}</th>
          </tr></thead>
          <tbody>
            ${result.custodies.map((c, i) => `
              <tr>
                <td>${i + 1}</td>
                <td style="font-weight:bold">${c.custodyNumber}</td>
                <td>${c.custodyDate.split('T')[0]}</td>
                <td>${isRTL ? (c.branchNameAr || '-') : (c.branchName || '-')}</td>
                <td>${c.accountCode ? c.accountCode + ' - ' + (isRTL ? (c.accountNameAr || '') : (c.accountName || '')) : '-'}</td>
                <td class="positive">${c.initialAmount.toFixed(3)}</td>
                <td class="negative">${c.totalSpent.toFixed(3)}</td>
                <td>${c.totalReturned > 0 ? c.totalReturned.toFixed(3) : '-'}</td>
                <td style="font-weight:bold;color:${c.currentBalance < 0 ? '#c62828' : '#333'}">${c.currentBalance.toFixed(3)}</td>
                <td>${(t as any)[c.status] || c.status}</td>
              </tr>
            `).join('')}
            <tr class="totals-row">
              <td colspan="5" style="text-align:${isRTL ? 'left' : 'right'};font-weight:bold;">${t.totals}</td>
              <td class="positive">${tt.totalInitialAmount.toFixed(3)}</td>
              <td class="negative">${tt.totalSpent.toFixed(3)}</td>
              <td>${tt.totalReturned > 0 ? tt.totalReturned.toFixed(3) : '-'}</td>
              <td style="font-weight:bold;color:${tt.totalCurrentBalance < 0 ? '#c62828' : '#1565c0'}">${tt.totalCurrentBalance.toFixed(3)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div class="signatures">
          <div class="sig-line"><div>${t.custodyHolder}</div></div>
          <div class="sig-line"><div>${t.accountant}</div></div>
          <div class="sig-line"><div>${t.manager}</div></div>
        </div>
        <div class="footer">${companyName} - ${new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</div>
      </body></html>
    `);
    pw.document.close();
    setTimeout(() => { pw.print(); }, 500);
  };

  // ============================================================
  // تصدير Excel
  // ============================================================
  const handleExportExcel = async () => {
    if (!result) return;
    const e = result.employee;
    const tt = result.totals;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(t.title);

    if (companyInfo?.logoBase64) {
      try {
        const base64Data = companyInfo.logoBase64.split(',')[1] || companyInfo.logoBase64;
        const ext = companyInfo.logoBase64.includes('png') ? 'png' : 'jpeg';
        const imgId = wb.addImage({ base64: base64Data, extension: ext });
        ws.addImage(imgId, { tl: { col: 4, row: 0 }, ext: { width: 80, height: 80 } });
      } catch (err) { console.error('Logo error:', err); }
    }

    ws.mergeCells('A1:J1');
    const compCell = ws.getCell('A1');
    compCell.value = isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || '');
    compCell.font = { size: 18, bold: true, color: { argb: 'FF1976D2' } };
    compCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(1).height = 35;

    ws.mergeCells('A2:J2');
    const titleCell = ws.getCell('A2');
    titleCell.value = `${t.title} - ${isRTL ? e.employeeNameAr : e.employeeName}`;
    titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
    ws.getRow(2).height = 28;

    ws.mergeCells('A3:J3');
    const statsCell = ws.getCell('A3');
    statsCell.value = `${t.totalCustodies}: ${e.totalCustodies}  |  ${t.openCustodies}: ${e.openCustodies}  |  ${t.totalInitial}: ${tt.totalInitialAmount.toFixed(3)}  |  ${t.totalSpent}: ${tt.totalSpent.toFixed(3)}  |  ${t.totalBalance}: ${tt.totalCurrentBalance.toFixed(3)}`;
    statsCell.font = { size: 10, color: { argb: 'FF555555' } };
    statsCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(3).height = 22;

    ws.getRow(4).height = 8;

    const headers = [t.rowNum, t.custodyNumber, t.date, t.branch, t.account, t.initialAmount, t.spent, t.returned, t.balance, t.status];
    const headerRow = ws.getRow(5);
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
    });
    headerRow.height = 24;

    let rowIdx = 6;
    result.custodies.forEach((c, i) => {
      const row = ws.getRow(rowIdx);
      row.getCell(1).value = i + 1;
      row.getCell(2).value = c.custodyNumber;
      row.getCell(2).font = { bold: true };
      row.getCell(3).value = c.custodyDate.split('T')[0];
      row.getCell(4).value = isRTL ? (c.branchNameAr || '-') : (c.branchName || '-');
      row.getCell(5).value = c.accountCode ? `${c.accountCode} - ${isRTL ? (c.accountNameAr || '') : (c.accountName || '')}` : '-';
      row.getCell(6).value = c.initialAmount;
      row.getCell(6).font = { color: { argb: 'FF2E7D32' }, bold: true };
      row.getCell(7).value = c.totalSpent;
      row.getCell(7).font = { color: { argb: 'FFC62828' }, bold: true };
      row.getCell(8).value = c.totalReturned > 0 ? c.totalReturned : null;
      row.getCell(9).value = c.currentBalance;
      row.getCell(9).font = { bold: true, color: { argb: c.currentBalance < 0 ? 'FFC62828' : 'FF1565C0' } };
      row.getCell(10).value = (t as any)[c.status] || c.status;

      const bgColor = rowIdx % 2 === 0 ? 'FFF5F5F5' : 'FFFFFFFF';
      row.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } } };
      });
      [6, 7, 8, 9].forEach(col => { row.getCell(col).numFmt = '#,##0.000'; });
      rowIdx++;
    });

    const totalsRow = ws.getRow(rowIdx);
    totalsRow.getCell(5).value = t.totals;
    totalsRow.getCell(5).font = { bold: true, size: 11 };
    totalsRow.getCell(5).alignment = { horizontal: isRTL ? 'left' : 'right', vertical: 'middle' };
    totalsRow.getCell(6).value = tt.totalInitialAmount;
    totalsRow.getCell(6).font = { bold: true, color: { argb: 'FF2E7D32' }, size: 11 };
    totalsRow.getCell(7).value = tt.totalSpent;
    totalsRow.getCell(7).font = { bold: true, color: { argb: 'FFC62828' }, size: 11 };
    totalsRow.getCell(8).value = tt.totalReturned > 0 ? tt.totalReturned : null;
    totalsRow.getCell(9).value = tt.totalCurrentBalance;
    totalsRow.getCell(9).font = { bold: true, color: { argb: tt.totalCurrentBalance < 0 ? 'FFC62828' : 'FF1565C0' }, size: 12 };
    totalsRow.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'double', color: { argb: 'FF1976D2' } }, bottom: { style: 'double', color: { argb: 'FF1976D2' } } };
    });
    [6, 7, 8, 9].forEach(col => { totalsRow.getCell(col).numFmt = '#,##0.000'; });

    ws.columns = [
      { width: 6 }, { width: 16 }, { width: 14 }, { width: 18 }, { width: 24 },
      { width: 16 }, { width: 16 }, { width: 14 }, { width: 16 }, { width: 12 },
    ];

    if (isRTL) ws.views = [{ rightToLeft: true, state: 'normal' }];

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EmployeeCustodySummary_${e.employeeName}_${new Date().getTime()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(t.exportSuccess, { position: 'top-center' });
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
    '& .MuiInputLabel-root': { fontWeight: 500 },
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* === Title === */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <PeopleIcon sx={{ fontSize: 36, color: '#1976d2' }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
          <Typography variant="body2" color="textSecondary">{t.subtitle}</Typography>
        </Box>
      </Box>

      {/* === Filters === */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete
          options={employees}
          getOptionLabel={(emp: any) => `${isRTL ? (emp.fullNameAr || emp.fullName) : emp.fullName} ${emp.employeeCode ? '(' + emp.employeeCode + ')' : ''}`}
          value={selectedEmployee}
          onChange={(_, v) => { setSelectedEmployee(v); setResult(null); }}
          sx={{ minWidth: 300, flex: 1 }}
          renderInput={(params) => <TextField {...params} label={t.selectEmployee} size="small" sx={inputSx} />}
        />
        <TextField
          select label={t.filterStatus} size="small" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140, ...inputSx }}
        >
          <MenuItem value="">{t.all}</MenuItem>
          <MenuItem value="Open">{t.open}</MenuItem>
          <MenuItem value="Settled">{t.settled}</MenuItem>
          <MenuItem value="Closed">{t.closed}</MenuItem>
        </TextField>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={isRTL ? 'من تاريخ' : 'From Date'}
            value={fromDate ? new Date(fromDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setFromDate(newVal);
              if (toDate && newVal && toDate < newVal) setToDate('');
            }}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={toDate ? new Date(toDate) : maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', sx: { width: 170, ...inputSx } } }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={isRTL ? 'إلى تاريخ' : 'To Date'}
            value={toDate ? new Date(toDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setToDate(newVal);
              if (fromDate && newVal && fromDate > newVal) setFromDate('');
            }}
            format="dd/MM/yyyy"
            minDate={fromDate ? new Date(fromDate) : minDate}
            maxDate={maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', sx: { width: 170, ...inputSx } } }}
          />
        </LocalizationProvider>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading || !selectedEmployee} sx={{ borderRadius: 2, fontWeight: 'bold', minWidth: 100 }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : t.search}
        </Button>
        <Tooltip title={t.reset}>
          <IconButton onClick={handleReset} color="default"><ReplayIcon /></IconButton>
        </Tooltip>
        {result && (
          <>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
              {t.print}
            </Button>
            <Button variant="outlined" color="success" startIcon={<ExcelIcon />} onClick={handleExportExcel} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
              {t.exportExcel}
            </Button>
          </>
        )}
      </Paper>

      {/* === No Data === */}
      {!result && !loading && (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <PeopleIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">{t.selectEmployeeFirst}</Typography>
        </Paper>
      )}

      {/* === Results === */}
      {result && (
        <>
          {/* Employee Name */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: isDarkMode ? 'grey.900' : 'grey.50', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <PeopleIcon sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold" color="primary">
              {isRTL ? result.employee.employeeNameAr : result.employee.employeeName}
            </Typography>
            <Chip label={`${result.employee.totalCustodies} ${t.totalCustodies}`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
            <Chip label={`${result.employee.openCustodies} ${t.open}`} size="small" color="info" sx={{ fontWeight: 'bold' }} />
            <Chip label={`${result.employee.settledCustodies} ${t.settled}`} size="small" color="success" sx={{ fontWeight: 'bold' }} />
          </Paper>

          {/* Summary Cards */}
          {(() => {
            const prev = result.previousBalance;
            const gInitial = result.totals.totalInitialAmount + (prev?.totalInitialAmount || 0);
            const gSpent = result.totals.totalSpent + (prev?.totalSpent || 0);
            const gReturned = result.totals.totalReturned + (prev?.totalReturned || 0);
            const gBalance = result.totals.totalCurrentBalance + (prev?.totalCurrentBalance || 0);
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                <Card elevation={3} sx={{ borderTop: '4px solid #2e7d32', borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <TrendingUpIcon sx={{ color: '#2e7d32', fontSize: 28, mb: 0.5 }} />
                    <Typography variant="caption" color="textSecondary" display="block">{t.totalInitial}</Typography>
                    <Typography variant="h5" fontWeight="bold" color="#2e7d32">{gInitial.toFixed(3)}</Typography>
                    <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
                  </CardContent>
                </Card>
                <Card elevation={3} sx={{ borderTop: '4px solid #c62828', borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <TrendingDownIcon sx={{ color: '#c62828', fontSize: 28, mb: 0.5 }} />
                    <Typography variant="caption" color="textSecondary" display="block">{t.totalSpent}</Typography>
                    <Typography variant="h5" fontWeight="bold" color="#c62828">{gSpent.toFixed(3)}</Typography>
                    <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
                  </CardContent>
                </Card>
                <Card elevation={3} sx={{ borderTop: '4px solid #4caf50', borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <MoneyIcon sx={{ color: '#4caf50', fontSize: 28, mb: 0.5 }} />
                    <Typography variant="caption" color="textSecondary" display="block">{t.totalReturned}</Typography>
                    <Typography variant="h5" fontWeight="bold" color="#4caf50">{gReturned.toFixed(3)}</Typography>
                    <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
                  </CardContent>
                </Card>
                <Card elevation={3} sx={{ borderTop: `4px solid ${gBalance < 0 ? '#c62828' : '#1976d2'}`, borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <WalletIcon sx={{ color: gBalance < 0 ? '#c62828' : '#1976d2', fontSize: 28, mb: 0.5 }} />
                    <Typography variant="caption" color="textSecondary" display="block">{t.totalBalance}</Typography>
                    <Typography variant="h5" fontWeight="bold" color={gBalance < 0 ? '#c62828' : '#1976d2'}>
                      {gBalance.toFixed(3)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
                  </CardContent>
                </Card>
              </Box>
            );
          })()}

          {/* Table */}
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 40 }}>{t.rowNum}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 130 }}>{t.custodyNumber}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 110 }}>{t.date}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>{t.branch}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>{t.account}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 120 }}>{t.initialAmount}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 120 }}>{t.spent}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 110 }}>{t.returned}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 120 }}>{t.balance}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 100 }}>{t.status}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.previousBalance && (
                  <TableRow sx={{ bgcolor: isDarkMode ? '#1b5e20' : '#e8f5e9' }}>
                    <TableCell colSpan={5} sx={{ textAlign: isRTL ? 'left' : 'right', fontWeight: 'bold', fontSize: 13 }}>
                      {isRTL ? 'رصيد سابق' : 'Previous Balance'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#2e7d32', fontSize: 13 }}>{result.previousBalance.totalInitialAmount.toFixed(3)}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#c62828', fontSize: 13 }}>{result.previousBalance.totalSpent.toFixed(3)}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontSize: 13 }}>{result.previousBalance.totalReturned > 0 ? result.previousBalance.totalReturned.toFixed(3) : '-'}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 13, color: result.previousBalance.totalCurrentBalance < 0 ? '#c62828' : '#1565c0' }}>
                      {result.previousBalance.totalCurrentBalance.toFixed(3)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
                {result.custodies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="textSecondary">{t.noData}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  result.custodies.map((c, idx) => (
                    <TableRow
                      key={c.custodyId}
                      sx={{
                        '&:nth-of-type(even)': { bgcolor: isDarkMode ? 'grey.900' : 'grey.50' },
                        '&:hover': { bgcolor: isDarkMode ? 'grey.800' : '#e3f2fd' },
                        transition: 'background 0.2s',
                      }}
                    >
                      <TableCell sx={{ textAlign: 'center' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#1976d2' }}>{c.custodyNumber}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{c.custodyDate.split('T')[0]}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{isRTL ? (c.branchNameAr || '-') : (c.branchName || '-')}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontSize: 12 }}>
                        {c.accountCode ? `${c.accountCode} - ${isRTL ? (c.accountNameAr || '') : (c.accountName || '')}` : '-'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#2e7d32', fontWeight: 'bold' }}>{c.initialAmount.toFixed(3)}</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#c62828', fontWeight: 'bold' }}>{c.totalSpent.toFixed(3)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{c.totalReturned > 0 ? c.totalReturned.toFixed(3) : '-'}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: c.currentBalance < 0 ? '#c62828' : isDarkMode ? '#90caf9' : '#1565c0' }}>
                        {c.currentBalance.toFixed(3)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={(t as any)[c.status] || c.status}
                          color={getStatusColor(c.status)}
                          size="small"
                          sx={{ fontWeight: 'bold', minWidth: 70 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {result.custodies.length > 0 && (() => {
                  const prev = result.previousBalance;
                  const grandInitial = result.totals.totalInitialAmount + (prev?.totalInitialAmount || 0);
                  const grandSpent = result.totals.totalSpent + (prev?.totalSpent || 0);
                  const grandReturned = result.totals.totalReturned + (prev?.totalReturned || 0);
                  const grandBalance = result.totals.totalCurrentBalance + (prev?.totalCurrentBalance || 0);
                  return (
                    <TableRow sx={{ bgcolor: isDarkMode ? '#1a237e' : '#e3f2fd' }}>
                      <TableCell colSpan={5} sx={{ textAlign: isRTL ? 'left' : 'right', fontWeight: 'bold', fontSize: 13 }}>{t.totals}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#2e7d32', fontSize: 13 }}>{grandInitial.toFixed(3)}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#c62828', fontSize: 13 }}>{grandSpent.toFixed(3)}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontSize: 13 }}>{grandReturned > 0 ? grandReturned.toFixed(3) : '-'}</TableCell>
                      <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 13, color: grandBalance < 0 ? '#c62828' : '#1565c0' }}>
                        {grandBalance.toFixed(3)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default EmployeeCustodySummaryPage;